/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import { IAdminVocabularyBookData, VOCABULARY_BOOK_LEVEL_LABEL } from '@datatype/admin/api-type';
import TextBlock from '@component/common/block/text-block';
import Chip from '@design-system/chip';

interface IProps {
  book: IAdminVocabularyBookData;
  isSelected: boolean;
  handleClick: () => void;
}

const CATEGORY_LABEL: Record<string, string> = {
  TEST: '시험준비',
  STUDY: '일반학습',
};

const Wrapper = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid ${(p) => (p.$selected ? color.BROWN_1 : color.GRAY_3)};
  background-color: ${(p) => (p.$selected ? color.BROWN_4 : color.WHITE_1)};
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    border-color: ${color.BROWN_1};
  }
`;

const TopRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const ChipGroup = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 6px;
  align-items: center;
`;

const Body = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 12px;
  align-items: center;
`;

const formatDate = (iso: string | null): string => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

const VocabularyBookItem: React.FC<IProps> = ({ book, isSelected, handleClick }) => {
  const categoryLabel = CATEGORY_LABEL[book.category] ?? book.category;
  const levelLabel = VOCABULARY_BOOK_LEVEL_LABEL[book.level] ?? book.level;
  const title = book.name ?? `${book.languageCode} ${categoryLabel} ${levelLabel} ${book.order}탄`;
  const publishedLabel = book.publishedAt ? `Published At: ${formatDate(book.publishedAt)}` : '미발행';

  return (
    <Wrapper $selected={isSelected} onClick={handleClick}>
      <TopRow>
        <ChipGroup>
          <Chip text={book.languageCode} size='tiny' isSelected theme={ETheme.BROWN} />
          <Chip text={categoryLabel} size='tiny' isSelected theme={ETheme.GRAY} />
        </ChipGroup>
        <ChipGroup>
          <Chip text={`${book.itemCount}개`} size='tiny' kind='outlined' isSelected theme={ETheme.GRAY} />
          <Chip
            text={publishedLabel}
            size='tiny'
            isSelected
            theme={book.publishedAt ? ETheme.GREEN : ETheme.RED}
          />
        </ChipGroup>
      </TopRow>
      <Body>
        <Chip text={levelLabel} size='tiny' isSelected theme={ETheme.YELLOW} />
        <Chip text={`order ${book.order}`} size='tiny' kind='outlined' isSelected theme={ETheme.GRAY} />
        <TextBlock
          text={title}
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true } }}
        />
      </Body>
    </Wrapper>
  );
};

export default VocabularyBookItem;
